/**
 * Configurable Workstream stage taxonomy.
 *
 * currentStage is CONFIGURABLE: the default pipeline stages are defined here
 * (not baked into the type system) so the pipeline can grow without a code
 * change to the domain types. The list ends with a "Custom" escape hatch so a
 * team can capture a stage outside the defaults.
 *
 * Pure TS — no React. Presentation layers map these to <Select> options and to
 * the board's stage columns; badge tones live in {@link ./workstreamModel}.
 */

/** A single configurable stage entry. */
export interface WorkstreamStageOption {
  /** Stored value (also the option value). */
  value: string;
  /** Human-readable label. */
  label: string;
  /** Optional one-line helper shown in pickers. */
  hint?: string;
}

/** Sentinel value for the "Custom / other" escape hatch. */
export const CUSTOM_STAGE = 'Custom';

/**
 * Default workstream stages, ordered along the delivery pipeline. The ordering
 * drives the "By stage" board column order and stage sorting.
 */
export const WORKSTREAM_STAGES: WorkstreamStageOption[] = [
  { value: 'Design', label: 'Design', hint: 'Shaping the approach' },
  { value: 'Build', label: 'Build', hint: 'Implementation in progress' },
  { value: 'Load Testing', label: 'Load Testing', hint: 'Validating under load' },
  {
    value: 'Production Rollout',
    label: 'Production Rollout',
    hint: 'Deploying to production',
  },
  { value: 'Complete', label: 'Complete', hint: 'Shipped / done' },
  { value: CUSTOM_STAGE, label: 'Custom', hint: 'Stage outside the defaults' },
];

/** Default stage for a brand-new workstream draft. */
export const DEFAULT_STAGE = 'Design';

/** Ordinal rank of a stage for board/column ordering (lower = earlier). Unknown = end. */
export function stageRank(stage: string): number {
  const idx = WORKSTREAM_STAGES.findIndex((s) => s.value === stage);
  return idx === -1 ? WORKSTREAM_STAGES.length : idx;
}

/** Human-readable label for a stage (falls back to the raw value). */
export function stageLabel(stage: string): string {
  return WORKSTREAM_STAGES.find((s) => s.value === stage)?.label ?? stage;
}
